/**
 * Create Test Suite Dialog
 * Modal for creating or editing test suites
 */

import { useState, useEffect } from 'react';
import { X, FolderPlus, Tag } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/i18n';
import type { EvalTestSuite, CreateTestSuiteRequest, UpdateTestSuiteRequest } from '@/types/evalScenarios';

interface CreateTestSuiteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTestSuiteRequest | UpdateTestSuiteRequest) => Promise<void>;
  suite?: EvalTestSuite | null;
  selectedScenarioIds?: string[];
  isSubmitting?: boolean;
}

export function CreateTestSuiteDialog({
  isOpen,
  onClose,
  onSubmit,
  suite,
  selectedScenarioIds = [],
  isSubmitting = false,
}: CreateTestSuiteDialogProps) {
  const { messages } = useI18n();
  const t = messages.evalScenarios?.testSuites || {
    createTitle: 'Create Test Suite',
    editTitle: 'Edit Test Suite',
    name: 'Name',
    namePlaceholder: 'Enter suite name',
    description: 'Description',
    descriptionPlaceholder: 'Enter description (optional)',
    tags: 'Tags',
    tagsPlaceholder: 'Type and press Enter to add',
    priority: 'Priority',
    scenarioCount: 'Scenarios included',
    cancel: 'Cancel',
    create: 'Create',
    save: 'Save',
  };

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tags: [] as string[],
    priority: 0,
    scenario_ids: [] as string[],
  });
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (suite) {
      setFormData({
        name: suite.name,
        description: suite.description || '',
        tags: suite.tags || [],
        priority: suite.priority,
        scenario_ids: suite.scenario_ids,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        tags: [],
        priority: 0,
        scenario_ids: selectedScenarioIds,
      });
    }
  }, [suite, selectedScenarioIds, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    const data = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      tags: formData.tags,
      priority: formData.priority,
      scenario_ids: formData.scenario_ids,
    };

    await onSubmit(data);
  };

  const handleAddTag = () => {
    const tag = newTag.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({ ...formData, tags: formData.tags.filter((t) => t !== tagToRemove) });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <FolderPlus className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-white">
                {suite ? t.editTitle : t.createTitle}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-white/20 transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.name} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t.namePlaceholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.description}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t.descriptionPlaceholder}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.tags}
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-violet-100 text-violet-700 text-sm rounded-full"
                >
                  <Tag className="w-3 h-3" />
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 hover:text-violet-900"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder={t.tagsPlaceholder}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm"
              />
              <Button type="button" variant="secondary" onClick={handleAddTag}>
                添加
              </Button>
            </div>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.priority}
            </label>
            <input
              type="number"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">数值越高越优先显示</p>
          </div>

          {/* Scenario Count */}
          <div className="bg-gray-50 rounded-lg p-3">
            <span className="text-sm text-gray-600">{t.scenarioCount}：</span>
            <span className="font-semibold text-violet-600">
              {formData.scenario_ids.length}
            </span>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="secondary" onClick={onClose}>
              {t.cancel}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || !formData.name.trim()}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  处理中...
                </span>
              ) : (
                suite ? t.save : t.create
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
